import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Lock, Thermometer, Zap, Wifi, WifiOff, Battery, BatteryWarning } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SmartSystems = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [devicesRes, statusRes] = await Promise.all([
        axios.get(`${API}/smart-systems/devices?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/smart-systems/status/${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDevices(devicesRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load smart systems data');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'smart_lock':
        return <Lock size={20} />;
      case 'thermostat':
        return <Thermometer size={20} />;
      case 'sensor':
        return <Zap size={20} />;
      default:
        return <Wifi size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'offline':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'error':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="smart-systems-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Smart Systems</h1>
          <p className="text-slate-600 mt-1">Manage smart locks, thermostats, and IoT devices</p>
        </div>
      </div>

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Devices</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{status.total_devices}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <Wifi size={18} />
                Online
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{status.online}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <WifiOff size={18} />
                Offline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{status.offline}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                <BatteryWarning size={18} />
                Low Battery
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">{status.low_battery_devices}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock size={20} />
              Smart Locks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-6xl font-bold text-blue-600 mb-2">
                {status?.devices_by_type?.smart_lock || 0}
              </div>
              <p className="text-slate-600">Devices</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Access Codes Active</span>
                <span className="font-semibold">N/A</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Avg Battery</span>
                <span className="font-semibold">85%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Thermometer size={20} />
              Thermostats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-6xl font-bold text-purple-600 mb-2">
                {status?.devices_by_type?.thermostat || 0}
              </div>
              <p className="text-slate-600">Devices</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Avg Temp</span>
                <span className="font-semibold">72°F</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Energy Savings</span>
                <span className="font-semibold text-emerald-600">15%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap size={20} />
              Sensors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-6xl font-bold text-amber-600 mb-2">
                {status?.devices_by_type?.sensor || 0}
              </div>
              <p className="text-slate-600">Devices</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Active Alerts</span>
                <span className="font-semibold">0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">All Online</span>
                <span className="font-semibold text-emerald-600">✓</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Devices</CardTitle>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Wifi className="mx-auto mb-2" size={40} />
              <p>No smart devices registered</p>
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => (
                <div key={device.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      device.status === 'online' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {getDeviceIcon(device.device_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{device.name}</span>
                        <Badge className={getStatusColor(device.status)}>
                          {device.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 capitalize">
                        {device.brand} • {device.device_type.replace('_', ' ')}
                      </p>
                    </div>
                  </div>
                  
                  {device.battery_level !== null && (
                    <div className="flex items-center gap-2">
                      {device.battery_level < 20 ? (
                        <BatteryWarning className="text-amber-500" size={20} />
                      ) : (
                        <Battery className="text-emerald-500" size={20} />
                      )}
                      <span className="text-sm font-medium">{device.battery_level}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Zap className="text-blue-600 mt-1" size={24} />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Smart Systems Benefits</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Keyless entry with temporary access codes for guests</li>
                <li>• Automated temperature control for energy savings</li>
                <li>• Real-time device monitoring and alerts</li>
                <li>• Remote lock/unlock capabilities</li>
                <li>• Energy consumption tracking and reporting</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SmartSystems;
